import { useEffect, useState } from "react";
import { g, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Timer, TrendingDown, Users, Receipt, Check } from "lucide-react";

const SHOTS = [
  ["sala", "Sala e tavoli", "La piantina del locale in tempo reale: coperti, conto in corso e minuti di attesa. I tavoli oltre i dieci minuti si accendono."],
  ["comanda", "Presa comanda", "Menu per categorie, allergeni sempre in vista, note per la cucina. Ogni piatto va alla portata e al reparto giusto."],
  ["monitor", "Monitor di reparto", "Cucina, pizzeria e bar vedono solo ciò che devono preparare, con i minuti trascorsi e l'avviso di sincronizzazione."],
  ["conto", "Cassa e conto", "Conto sempre aggiornato, sconti tracciati, divisione per persone o per singoli piatti."],
];

const KPI = [
  [Timer, "−6 min", "sul tempo medio di attesa al tavolo", "I minuti di attesa sono sempre a schermo e il tavolo si accende dopo dieci minuti: il ritardo si vede prima che il cliente si lamenti."],
  [TrendingDown, "−30%", "sugli errori di comanda", "Il piatto parte già assegnato a portata e reparto, con nota e allergeni: si eliminano i foglietti e i passaggi a voce."],
  [Receipt, "−4 min", "per chiudere un conto diviso", "Divisione per persone o per singoli piatti con incassi parziali: alla cassa non si fanno più calcoli a mano."],
  [Users, "1 turno", "per formare una persona nuova", "Accesso con PIN a quattro cifre e schermata dedicata al ruolo: chi entra vede solo ciò che gli serve."],
];

/** Caso studio stampabile (A4) da allegare al sito o inviare via email. */
export default function CasoStudio({ onBack }) {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    g("/plan").then((d) => setPlans(d.plans || [])).catch(() => {});
  }, []);
  const std = plans.find((p) => p.id === "standard");

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0" data-testid="case-study-page">
      <div className="mx-auto mb-5 flex max-w-[900px] items-center justify-between gap-3 px-5 print:hidden">
        <Button variant="ghost" onClick={onBack} data-testid="case-back-btn">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Torna alla presentazione
        </Button>
        <Button onClick={() => window.print()} data-testid="case-print-btn">
          <Printer className="mr-1.5 h-4 w-4" /> Stampa o salva in PDF
        </Button>
      </div>

      <article className="mx-auto max-w-[900px] bg-white px-10 py-12 shadow-xl print:max-w-none print:shadow-none sm:px-14">
        <header className="border-b-2 border-slate-900 pb-7">
          <div className="flex items-baseline justify-between gap-4">
            <p className="font-head text-lg font-bold tracking-tight">Reprehendo <span className="text-amber-600">RH</span></p>
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Caso studio</p>
          </div>
          <h1 className="font-head mt-6 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            Un servizio da 90 coperti, senza foglietti e senza urla in cucina.
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
            Come una trattoria-pizzeria con tre reparti, asporto e delivery organizza il servizio con Reprehendo:
            comande divise per portate, monitor di reparto, conti separati e magazzino che si scarica da solo.
          </p>
        </header>

        <section className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-3">
          {[["Il locale", "Trattoria e pizzeria, 90 coperti interni più dehors, tre reparti: cucina, pizzeria, bar. Asporto e due piattaforme di delivery."],
            ["Il problema", "Comande su carta, piatti che arrivano scoordinati, tavoli dimenticati nelle serate piene, conti divisi calcolati a mano alla cassa."],
            ["La scelta", "Un unico strumento su telefono e tablet, con accessi per ruolo e monitor in cucina: niente hardware dedicato, niente installazioni."]].map(([t, d]) => (
            <div key={t}>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-600">{t}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">{d}</p>
            </div>
          ))}
        </section>

        <section className="mt-12">
          <h2 className="font-head text-lg font-bold text-slate-900">Come cambia il servizio</h2>
          <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2">
            {KPI.map(([Icon, value, label, why]) => (
              <div key={label} className="flex gap-4 border-t border-slate-200 pt-4">
                <span className="mt-0.5 shrink-0 rounded-lg bg-amber-50 p-2">
                  <Icon className="h-4 w-4 text-amber-600" />
                </span>
                <div className="min-w-0">
                  <p className="font-num text-2xl font-bold leading-none text-slate-900">{value}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{why}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
            I valori indicati sono uno scenario di riferimento costruito sui tempi misurati dal gestionale in un
            servizio tipo, non risultati certificati di un cliente specifico.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-head text-lg font-bold text-slate-900">Il gestionale al lavoro</h2>
          <div className="mt-5 space-y-8">
            {SHOTS.map(([k, title, desc], i) => (
              <figure key={k} className="break-inside-avoid" data-testid={`case-shot-${k}`}>
                <img src={`/screens/${k}.png`} alt={title} className="w-full rounded-lg border border-slate-200" />
                <figcaption className="mt-3 flex gap-3">
                  <span className="font-num text-xs font-bold text-amber-600">0{i + 1}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-slate-900">{title}</span>
                    <span className="block text-sm leading-relaxed text-slate-600">{desc}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-12 break-inside-avoid">
          <h2 className="font-head text-lg font-bold text-slate-900">Cosa è incluso</h2>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {["Comande divise in cinque portate e tre reparti",
              "Monitor di cucina, pizzeria e bar con lettura vocale",
              "Avvisi sui tavoli in attesa e sui reparti in ritardo",
              "Conti separati per persona o per singolo piatto",
              "Asporto e delivery con commissioni per canale",
              "Magazzino con ricette e scarico automatico",
              "Report vendite, storico ordini e rubrica clienti",
              "Accessi per ruolo con PIN a quattro cifre"].map((f) => (
              <p key={f} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" /> {f}
              </p>
            ))}
          </div>
        </section>

        <footer className="mt-12 break-inside-avoid border-t-2 border-slate-900 pt-7">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="min-w-0">
              <p className="font-head text-lg font-bold text-slate-900">Prova con i tuoi piatti, non con una demo generica.</p>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                Rispondi a sei domande e ricevi la tua app già configurata per sette giorni: reparti, zone e menu
                di partenza secondo la tua attività. Nessuna carta di credito.
              </p>
            </div>
            {std && (
              <div className="shrink-0 rounded-lg border border-slate-200 p-4 text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Piano Standard</p>
                <p className="font-num mt-1 text-2xl font-bold text-slate-900">{eur(std.price)}<span className="text-xs font-normal text-slate-500">/mese</span></p>
                {std.price_year && <p className="text-xs font-semibold text-amber-600">{eur(std.price_year)}/anno · 2 mesi gratis</p>}
              </div>
            )}
          </div>
          <p className="mt-7 text-[11px] text-slate-400">
            Reprehendo (RH) — gestionale per ristoranti, pizzerie e bar · documento a uso commerciale, non fiscale.
          </p>
        </footer>
      </article>
    </div>
  );
}
